import useUrlState from '@ahooksjs/use-url-state'
import { useRequest, useTitle } from 'ahooks'
import { Button, Spin, Tabs } from 'antd'
import { useAtom } from 'jotai'
import { assign, merge } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { RouteChildrenProps } from 'react-router-dom'

import { hasPointPagePermission, LockIcon, NoPermissionPage, PERMISSIONS } from '@/components'
import { ReportContainer } from '@/components/reports/report.container'
// import { ResourceEvaluationContainer } from '@/components/resource-evaluation'
import { Header } from '@/modules/header'
import { ProjectResourceDashboard } from '@/modules/project/project-detail/project-dashboard/project-resource-dashboard'
import { userInfoAtom } from '@/modules/user'
import { PAGE_RESOURCE_POINTS_ENUM, Project, ResourceUser } from '@/schemas'
import { ga, traceEvent } from '@/utils/ga'

import { PpFileContainer } from '../../../components/files'
import { ProjectClone } from '../project-create/project-clone.component'
import { ProjectHeaderContainer } from './project-header'
import { ProjectJQLContainer } from './project-jql/project-jql.container'
import { ProjectStagesContainer } from './project-stages'
import { ProjectSummaryContainer } from './project-summary'
import { ProjectWorkResourceContainer } from './project-work-resource/project-work-resource.container'

const { TabPane } = Tabs

export const ProjectDetailContainer: React.FC<RouteChildrenProps<{ projectId: string }>> = ({ match }) => {
  const { t } = useTranslation()
  const projectId = match!.params.projectId
  const [viewer] = useAtom(userInfoAtom)

  const reportRef = React.useRef<{
    entryTime: number
    name?: string
    entry?: string
    users?: ResourceUser[]
  }>({
    entryTime: +new Date(),
  })
  const [users, setUsers] = React.useState<ResourceUser[]>()
  const [prevTab, setPrevTab] = React.useState<string>()
  const [cloneVisible, setCloneVisible] = React.useState(false)

  const headerRef = React.useRef<HTMLDivElement>(null)
  const tabRef = React.useRef<HTMLDivElement>(null)

  const [state, setState] = useUrlState(
    { tab: new URLSearchParams(window.location.search).get('tab') || 'detail' },
    {
      navigateMode: 'replace',
    }
  )

  const { data: project, refresh } = useRequest<Project>(
    {
      url: '/api/project/getProjectById',
      params: {
        selfId: projectId,
      },
    },
    {
      refreshDeps: [projectId],
    }
  )

  useTitle(project ? project.name : document.title, { restoreOnUnmount: true })

  const reportExposeTime = () => {
    const report = reportRef.current
    if (report.name && report.entry && report.entryTime) {
      ga('event', traceEvent.detailPage.events.expose_time_project_detail, {
        event_label: report.name,
        event_value: Math.round((+new Date() - report.entryTime) / 1000),
        event_category: report.entry,
        action: traceEvent.detailPage.action,
        target: traceEvent.detailPage.targets.ef_pmo_project_detail,
        payload: report.users
          ? report.users.some((user: ResourceUser) => user.email === viewer.email)
            ? '1'
            : '0'
          : '',
      })
    }
  }

  React.useEffect(() => {
    const entry = window.sessionStorage.getItem('entry')
    reportRef.current = assign(reportRef.current, {
      entry: entry || 'ef_pmo_project_detail',
    })
    sessionStorage.removeItem('entry')

    window.addEventListener('beforeunload', reportExposeTime)
    return () => {
      window.removeEventListener('beforeunload', reportExposeTime)
      reportExposeTime()
    }
  }, [])

  React.useEffect(() => {
    if (project) {
      reportRef.current = assign(reportRef.current, { name: project.name })
    }
  }, [project])

  React.useEffect(() => {
    if (users) {
      reportRef.current = assign(reportRef.current, { users })
    }
  }, [users])

  React.useEffect(() => {
    if (project && prevTab !== state.tab) {
      switch (state.tab) {
        case 'detail':
          ga('event', traceEvent.projectTab.events.click_switch_tab, {
            event_category: traceEvent.projectTab.event_categories.ef_pmo_project_detail,
            action: traceEvent.projectTab.action,
            event_label: project.name,
            target: traceEvent.projectTab.targets.overview,
          })
          break
        case 'stage':
          ga('event', traceEvent.projectTab.events.click_switch_tab, {
            event_category: traceEvent.projectTab.event_categories.ef_pmo_project_detail,
            action: traceEvent.projectTab.action,
            event_label: project.name,
            target: traceEvent.projectTab.targets.stages_and_tasks,
          })
          break
        case 'userResource':
          ga('event', traceEvent.projectTab.events.click_switch_tab, {
            event_category: traceEvent.projectTab.event_categories.ef_pmo_project_detail,
            action: traceEvent.projectTab.action,
            event_label: project.name,
            target: traceEvent.projectTab.targets.resource,
          })
          break
        case 'list':
          ga('event', traceEvent.projectTab.events.click_switch_tab, {
            event_category: traceEvent.projectTab.event_categories.ef_pmo_project_detail,
            action: traceEvent.projectTab.action,
            event_label: project.name,
            target: traceEvent.projectTab.targets.issue_list,
          })
          break
        case 'report':
          ga('event', traceEvent.projectTab.events.click_switch_tab, {
            event_category: traceEvent.projectTab.event_categories.ef_pmo_project_detail,
            action: traceEvent.projectTab.action,
            event_label: project.name,
            target: traceEvent.projectTab.targets.weekly_reports,
          })
          break
        case 'file':
          ga('event', traceEvent.projectTab.events.click_switch_tab, {
            event_category: traceEvent.projectTab.event_categories.ef_pmo_project_detail,
            action: traceEvent.projectTab.action,
            event_label: project.name,
            target: traceEvent.projectTab.targets.files,
          })
          break
        case 'finish':
          ga('event', traceEvent.projectTab.events.click_switch_tab, {
            event_category: traceEvent.projectTab.event_categories.ef_pmo_project_detail,
            action: traceEvent.projectTab.action,
            event_label: project.name,
            target: traceEvent.projectTab.targets.evaluation,
          })
          break
      }
      setPrevTab(state.tab)
    }
  }, [state, project])

  if (!project) {
    return (
      <div className='flex h-full justify-center items-center'>
        <Spin size='large' delay={300} />
      </div>
    )
  }

  return (
    <>
      <div style={{ position: 'sticky', top: 0, zIndex: 3, background: 'white' }} ref={headerRef}>
        <Header
          title={<ProjectHeaderContainer project={project} refreshProject={refresh} users={users} />}
          destroyInactiveTabPane
          actions={[
            <>
              <Button type='primary' size='small' onClick={() => setCloneVisible(true)}>
                {t('project.clone.button.title')}
              </Button>
              <ProjectClone
                originProject={project}
                visible={cloneVisible}
                onCancel={() => {
                  setCloneVisible(false)
                }}
              />
            </>,
          ]}
        />
      </div>
      <div ref={tabRef}>
        <Tabs
          destroyInactiveTabPane
          activeKey={state.tab}
          onChange={(tab) => {
            const keys = Object.keys(state)
            setState({
              ...merge({}, ...keys.map((key) => ({ [key]: undefined }))), // reset url params
              tab,
            })
            if (tab === 'detail') refresh()
          }}
        >
          <TabPane
            tab={
              <span>
                {t('overview')}
                {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.概况, PERMISSIONS.READ, project.permissions) && (
                  <LockIcon />
                )}
              </span>
            }
            key='detail'
          ></TabPane>
          <TabPane
            tab={
              <span>
                {t('stage issue')}
                {!hasPointPagePermission(
                  PAGE_RESOURCE_POINTS_ENUM.阶段与任务,
                  PERMISSIONS.READ,
                  project.permissions
                ) && <LockIcon />}
              </span>
            }
            key='stage'
          ></TabPane>
          <TabPane
            tab={
              <span>
                {t('resource dashboard')}
                {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源看板, PERMISSIONS.READ, project.permissions) && (
                  <LockIcon />
                )}
              </span>
            }
            key='dashboard'
          ></TabPane>
          <TabPane
            tab={
              <span>
                {t('user resource')}
                {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, PERMISSIONS.READ, project.permissions) && (
                  <LockIcon />
                )}
              </span>
            }
            key='userResource'
          ></TabPane>
          <TabPane
            tab={
              <span>
                {t('requirement problem list')}
                {!hasPointPagePermission(
                  PAGE_RESOURCE_POINTS_ENUM.需求与问题,
                  PERMISSIONS.READ,
                  project.permissions
                ) && <LockIcon />}
              </span>
            }
            key='list'
          ></TabPane>
          <TabPane
            tab={
              <span>
                {t('reports')}
                {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.周报, PERMISSIONS.READ, project.permissions) && (
                  <LockIcon />
                )}
              </span>
            }
            key='report'
          ></TabPane>
          <TabPane
            tab={
              <span>
                {t('file')}
                {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.文件, PERMISSIONS.READ, project.permissions) && (
                  <LockIcon />
                )}
              </span>
            }
            key='file'
          ></TabPane>
          {/* {project.status === ProjectStatus.已结项 && (
            <TabPane
              tab={
                <span>
                  {t('project.evaluation')}
                  {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.结项, PERMISSIONS.READ, project.permissions) && (
                    <LockIcon />
                  )}
                </span>
              }
              key='finish'
            ></TabPane>
          )} */}
        </Tabs>
      </div>
      {state.tab === 'detail' &&
        (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.概况, PERMISSIONS.READ, project.permissions) ? (
          <ProjectSummaryContainer project={project} setUsers={setUsers} />
        ) : (
          <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.概况} />
        ))}
      {state.tab === 'stage' &&
        (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.阶段与任务, PERMISSIONS.READ, project.permissions) ? (
          <ProjectStagesContainer project={project} refreshProject={refresh} />
        ) : (
          <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.阶段与任务} />
        ))}
      {state.tab === 'dashboard' &&
        (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源看板, PERMISSIONS.READ, project.permissions) ? (
          <ProjectResourceDashboard selfId={project.selfId} project={project} />
        ) : (
          <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.资源看板} />
        ))}
      {state.tab === 'userResource' &&
        (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, PERMISSIONS.READ, project.permissions) ? (
          <ProjectWorkResourceContainer project={project} />
        ) : (
          <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.资源管理} />
        ))}
      {state.tab === 'report' &&
        (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.周报, PERMISSIONS.READ, project.permissions) ? (
          <ReportContainer
            selfId={project.selfId}
            type={project.type}
            name={project.name}
            status={project.status}
            permissions={project.permissions}
            refreshProject={refresh}
          />
        ) : (
          <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.周报} />
        ))}
      {state.tab === 'list' &&
        (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.需求与问题, PERMISSIONS.READ, project.permissions) ? (
          <ProjectJQLContainer project={project} />
        ) : (
          <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.需求与问题} />
        ))}
      {state.tab === 'file' &&
        (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.文件, PERMISSIONS.READ, project.permissions) ? (
          <PpFileContainer pp={project} />
        ) : (
          <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.文件} />
        ))}
      {/* {state.tab === 'finish' &&
        (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.结项, PERMISSIONS.READ, project.permissions) ? (
          <ResourceEvaluationContainer resource={project} onRefresh={refresh} />
        ) : (
          <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.结项} />
        ))} */}
    </>
  )
}
